import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ConnectionModal = ({ isOpen, onClose, onConnect }) => {
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const modalRef = useRef(null);

  const institutions = [
  {
    id: 'chase',
    name: 'Chase Bank',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_16dd95acd-1766984880228.png",
    logoAlt: 'Chase Bank logo with blue octagonal shape on white background',
    type: 'Bank',
    popularity: 'Most Popular'
  },
  {
    id: 'bofa',
    name: 'Bank of America',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1618a63ea-1764656767804.png",
    logoAlt: 'Bank of America logo with red and blue flag design',
    type: 'Bank',
    popularity: 'Popular'
  },
  {
    id: 'wells',
    name: 'Wells Fargo',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_138289581-1764637918824.png",
    logoAlt: 'Wells Fargo logo with red and yellow stagecoach icon',
    type: 'Bank',
    popularity: 'Popular'
  },
  {
    id: 'amex',
    name: 'American Express',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12d055e2e-1767320427031.png",
    logoAlt: 'American Express logo with blue square and white text',
    type: 'Credit Card',
    popularity: 'Popular'
  },
  {
    id: 'capital',
    name: 'Capital One',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1300aadc2-1764637919060.png",
    logoAlt: 'Capital One logo with red and white swoosh design',
    type: 'Bank & Credit',
    popularity: 'Popular'
  },
  {
    id: 'citi',
    name: 'Citibank',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1862ce206-1764637921073.png",
    logoAlt: 'Citibank logo with blue and red arc design',
    type: 'Bank',
    popularity: 'Popular'
  }];


  const mockAccounts = [
  { id: 'acc1', name: 'Checking Account', number: '****1234', balance: 5420.50 },
  { id: 'acc2', name: 'Savings Account', number: '****5678', balance: 12350.75 },
  { id: 'acc3', name: 'Credit Card', number: '****9012', balance: -1250.00 }];


  const filteredInstitutions = institutions?.filter((inst) =>
  inst?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  inst?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef?.current && !modalRef?.current?.contains(e?.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setStep(1);
    setSearchQuery('');
    setSelectedInstitution(null);
    setCredentials({ username: '', password: '' });
    setSelectedAccounts([]);
    onClose();
  };

  const handleInstitutionSelect = (institution) => {
    setSelectedInstitution(institution);
    setStep(2);
  };

  const handleCredentialsSubmit = () => {
    if (credentials?.username && credentials?.password) {
      setStep(3);
    }
  };

  const handleAccountToggle = (accountId) => {
    setSelectedAccounts((prev) =>
    prev?.includes(accountId) ?
    prev?.filter((id) => id !== accountId) :
    [...prev, accountId]
    );
  };

  const handleConnect = () => {
    if (selectedAccounts?.length > 0) {
      onConnect({
        institution: selectedInstitution,
        accounts: mockAccounts?.filter((acc) => selectedAccounts?.includes(acc?.id))
      });
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-start justify-center pt-[8vh] px-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose} />
      <div
        ref={modalRef}
        className="relative w-full max-w-2xl bg-card rounded-2xl shadow-elevation-5 max-h-[85vh] overflow-hidden flex flex-col">
        
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {step === 1 && 'Connect Financial Account'}
              {step === 2 && 'Enter Credentials'}
              {step === 3 && 'Select Accounts'}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {step === 1 && 'Search and select your financial institution'}
              {step === 2 && 'Securely authenticate with your institution'}
              {step === 3 && 'Choose accounts to sync with FINVISAR'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0"
            aria-label="Close modal">
            
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {step === 1 &&
          <div className="space-y-4 md:space-y-6">
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                      Bank-Grade Security
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Your credentials are encrypted with 256-bit AES encryption and never stored on our servers. We use OAuth 2.0 and comply with SOC 2 standards.
                    </p>
                  </div>
                </div>
              </div>

              <Input
              type="search"
              placeholder="Search for your bank or credit card..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="mb-4" />
            

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {filteredInstitutions?.map((institution) =>
              <div
                key={institution?.id}
                onClick={() => handleInstitutionSelect(institution)}
                className="flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-muted rounded-xl cursor-pointer hover:bg-muted/80 transition-smooth border border-transparent hover:border-primary">
                
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-background flex items-center justify-center overflow-hidden flex-shrink-0">
                      <Image
                    src={institution?.logo}
                    alt={institution?.logoAlt}
                    className="w-full h-full object-cover" />
                  
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground truncate">
                        {institution?.name}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {institution?.type} • {institution?.popularity}
                      </p>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground flex-shrink-0" />
                  </div>
              )}
              </div>
            </div>
          }

          {step === 2 &&
          <div className="space-y-4 md:space-y-6">
              <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-muted rounded-xl">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-background flex items-center justify-center overflow-hidden flex-shrink-0">
                  <Image
                  src={selectedInstitution?.logo}
                  alt={selectedInstitution?.logoAlt}
                  className="w-full h-full object-cover" />
                
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm md:text-base font-semibold text-foreground">
                    {selectedInstitution?.name}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {selectedInstitution?.type}
                  </p>
                </div>
              </div>

              <div className="bg-warning/10 border border-warning/20 rounded-xl p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                      Demo Credentials
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground mb-2">
                      Use these mock credentials for testing:
                    </p>
                    <div className="space-y-1 text-xs md:text-sm font-mono bg-background/50 p-2 rounded">
                      <div>Username: <span className="text-foreground font-semibold">demo_user</span></div>
                      <div>Password: <span className="text-foreground font-semibold">Demo@123</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Input
                label="Username or Email"
                type="text"
                placeholder="Enter your username"
                value={credentials?.username}
                onChange={(e) => setCredentials({ ...credentials, username: e?.target?.value })}
                required />
              

                <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={credentials?.password}
                onChange={(e) => setCredentials({ ...credentials, password: e?.target?.value })}
                required />
              
              </div>

              <div className="flex items-start gap-2 text-xs md:text-sm text-muted-foreground">
                <Icon name="Lock" size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  Your credentials are transmitted securely using TLS 1.3 encryption and are never stored by FINVISAR.
                </p>
              </div>
            </div>
          }

          {step === 3 &&
          <div className="space-y-4 md:space-y-6">
              <div className="bg-success/10 border border-success/20 rounded-xl p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                      Authentication Successful
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      We found {mockAccounts?.length} accounts. Select the ones you want to sync.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {mockAccounts?.map((account) =>
              <div
                key={account?.id}
                onClick={() => handleAccountToggle(account?.id)}
                className={`flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl cursor-pointer transition-smooth border ${
                selectedAccounts?.includes(account?.id) ?
                'bg-primary/10 border-primary' : 'bg-muted border-transparent hover:border-border'}`
                }>
                
                    <div className={`w-5 h-5 md:w-6 md:h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                selectedAccounts?.includes(account?.id) ?
                'bg-primary border-primary' : 'border-border'}`
                }>
                      {selectedAccounts?.includes(account?.id) &&
                  <Icon name="Check" size={14} color="white" />
                  }
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground">
                        {account?.name}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {account?.number}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-sm md:text-base font-semibold ${
                  account?.balance < 0 ? 'text-error' : 'text-foreground'}`
                  }>
                        {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })?.format(Math.abs(account?.balance))}
                      </p>
                    </div>
                  </div>
              )}
              </div>
            </div>
          }
        </div>

        <div className="flex items-center justify-between gap-3 p-4 md:p-6 border-t border-border flex-shrink-0">
          {step > 1 &&
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            iconName="ChevronLeft"
            iconPosition="left">
            
              Back
            </Button>
          }
          <div className="flex-1" />
          {step === 2 &&
          <Button
            variant="default"
            onClick={handleCredentialsSubmit}
            disabled={!credentials?.username || !credentials?.password}
            iconName="ArrowRight"
            iconPosition="right">
            
              Continue
            </Button>
          }
          {step === 3 &&
          <Button
            variant="default"
            onClick={handleConnect}
            disabled={selectedAccounts?.length === 0}
            iconName="Link"
            iconPosition="left">
            
              Connect {selectedAccounts?.length} Account{selectedAccounts?.length !== 1 ? 's' : ''}
            </Button>
          }
        </div>
      </div>
    </div>);

};

export default ConnectionModal;